import { Wrench, ShieldCheck, Gauge, Cog, Radio, Youtube, ExternalLink } from "lucide-react";
import { MECCANICA_VIDEOS, YT_CHANNEL } from "@/lib/constants";

const STEPS = [
  { icon: Cog, title: "1 · Scocca nuda", text: "L'auto stradale viene spogliata di tutto: sedili, plance, insonorizzanti e vetri. Resta la sola scocca, alleggerita e pronta per essere rinforzata." },
  { icon: ShieldCheck, title: "2 · Gabbia di sicurezza", text: "Un roll-cage in tubi d'acciaio saldato all'abitacolo protegge l'equipaggio e irrigidisce il telaio, aumentando la rigidità torsionale della vettura." },
  { icon: Gauge, title: "3 · Motore e trazione", text: "Motore, turbo e trasmissione vengono elaborati per reggere lo stress agonistico. Le Rally1 spingono 1.6 turbo da circa 380 CV su qualsiasi fondo." },
  { icon: Wrench, title: "4 · Sospensioni", text: "Ammortizzatori a lungo escursione e regolabili gestiscono i fondi sconnessi di terra e asfalto, mantenendo la vettura prevedibile al limite." },
  { icon: ShieldCheck, title: "5 · Sicurezza abitacolo", text: "Sedili avvolgenti, cinture a più punti, impianto di estinzione e schiume antiurto completano la trasformazione secondo le norme FIA." },
  { icon: Radio, title: "6 · Pace notes & recce", text: "Pilota e navigatore preparano le note nella ricognizione: ogni curva, salto e insidia viene codificata per affrontare la prova in piena fiducia." },
];

export default function Meccanica() {
  return (
    <div data-testid="meccanica-page">
      {/* Hero */}
      <div className="bg-gradient-to-b from-legend-ink to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 py-14">
          <span className="inline-flex items-center gap-2 font-mono uppercase text-xs tracking-[0.3em] text-legend-yellow mb-3"><Wrench size={14} /> Tecnica & Officina</span>
          <h1 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-none">La Meccanica del Rally</h1>
          <p className="text-slate-300 max-w-2xl mt-4 text-base">Come nasce un'auto da corsa: dalla scocca stradale alla vettura da prova speciale. Video, passaggi chiave e il mestiere dell'equipaggio, raccontati dalla redazione di Legend Magazine.</p>
        </div>
      </div>

      {/* Build steps */}
      <section className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex items-center gap-3 mb-6">
          <span className="h-6 w-1.5 bg-legend-red" />
          <h2 className="font-display font-black uppercase text-2xl tracking-wide">Dalla strada alla prova speciale</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {STEPS.map((s, i) => (
            <div key={i} data-testid={`meccanica-step-${i}`} className="rounded-sm border border-slate-200 dark:border-slate-800 bg-white dark:bg-legend-paper p-5 hover:border-legend-red transition-colors">
              <s.icon size={22} className="text-legend-red mb-3" />
              <h3 className="font-display font-bold uppercase text-lg leading-tight">{s.title}</h3>
              <p className="text-sm text-slate-600 dark:text-slate-300 mt-2">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Videos */}
      <section className="max-w-7xl mx-auto px-4 pb-16">
        <div className="flex items-center gap-3 mb-6">
          <span className="h-6 w-1.5 bg-legend-red" />
          <h2 className="font-display font-black uppercase text-2xl tracking-wide flex items-center gap-2"><Youtube size={24} className="text-legend-red" /> Video: come si costruisce e come funziona</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {MECCANICA_VIDEOS.map((v, i) => (
            <div key={v.id} data-testid={`meccanica-video-${i}`} className="rounded-sm overflow-hidden border border-slate-200 dark:border-slate-800 bg-white dark:bg-legend-paper">
              <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src={`https://www.youtube.com/embed/${v.id}`}
                  title={v.title}
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="p-4">
                <h3 className="font-display font-bold uppercase text-base leading-tight">{v.title}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">{v.note}</p>
              </div>
            </div>
          ))}
        </div>

        <a href={YT_CHANNEL} target="_blank" rel="noreferrer" data-testid="meccanica-yt-channel" className="mt-8 inline-flex items-center gap-2 bg-legend-red hover:bg-legend-dark text-white font-bold uppercase tracking-wide px-6 py-3 rounded-sm transition-colors">
          <Youtube size={18} /> Altri video sul canale Legend Team <ExternalLink size={15} />
        </a>
      </section>
    </div>
  );
}
